import {
  defineNuxtRouteMiddleware,
  navigateTo,
  showError,
  useError,
} from "nuxt/app";
import {
  useBugReporter,
  usePinia,
  useFeatureFlag,
  useDirectBackend,
} from "#imports";
import { getQueryParamValue } from "@chatfood/core-utils";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { IDomainOrderingModeSettings } from "~/domain/ordering-mode-settings";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useCustomerStore } from "~/stores/customer";
import { useDiscountsStore } from "~/stores/discounts";
import { useMenusStore } from "~/stores/menus";

const abortWithUnexpectedError = (
  message: string = "We could not load the store details. Please try again in a few minutes.",
) => {
  throw showError({
    statusCode: 500,
    statusMessage: message,
  });
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (useError().value) return;

  const { isEnabled } = useFeatureFlag();
  const isCommerceApiEnabled = await isEnabled("direct.useCommerceApi");

  // Skip if Commerce API is not enabled (old middleware will handle the outlet load)
  if (!isCommerceApiEnabled) return;

  const routeOutlet = getQueryParamValue(to.params, "outletSlug");

  if (!routeOutlet) {
    return abortWithUnexpectedError(
      "EnsureStoreIsLoadedMiddleware requires a route with outletSlug parameter.",
    );
  }

  const pinia = usePinia();
  const outlet = useOutletStore(pinia);

  // Skip if outlet and ordering mode settings are already loaded for this route
  if (
    outlet.isLoaded &&
    outlet.slug === routeOutlet &&
    outlet.isLoadedOrderingModeSettings
  ) {
    return;
  }

  const business = useBusinessStore(pinia);
  const customer = useCustomerStore(pinia);
  const discounts = useDiscountsStore(pinia);
  const menus = useMenusStore(pinia);

  const { storeRepo } = useDirectBackend();

  const routeOrderingMode = (
    Array.isArray(to.params.orderingMode)
      ? to.params.orderingMode[0]
      : to.params.orderingMode
  ) as IDomainOrderingMode;

  if (!routeOrderingMode) {
    return abortWithUnexpectedError(
      "EnsureStoreIsLoadedMiddleware requires a route with orderingMode parameter.",
    );
  }

  outlet.patch({ activeOrderingMode: routeOrderingMode });

  type StoreResponse = Awaited<ReturnType<typeof storeRepo.getStore>>;

  try {
    const response: StoreResponse = await storeRepo.getStore({
      accountId: business.accountId,
      channelLinkId: routeOutlet,
    });

    if (!response) return;

    // Map Store API response to IDomainOrderingModeSettings
    const mapModeSettings = (
      mode: "delivery" | "pickup" | "curbside",
      modeData:
        | StoreResponse["settings"]["delivery"]
        | StoreResponse["settings"]["pickup"]
        | StoreResponse["settings"]["curbside"],
    ): IDomainOrderingModeSettings => ({
      mode,
      isEnabled: modeData.enabled,
      outletState: modeData.status,
      acceptsAsapOrders: modeData.scheduling.acceptsAsapOrders,
      acceptsPreOrders: modeData.scheduling.acceptsPreOrders,
      acceptsPackaging: false, // Store API does not provide this information, defaulting to false
      paymentMethods: [], // Store API does not provide this information, defaulting to empty array
      preparationMinutes: modeData.preparationMinutes || 0,
      estimatedCompletionMinutes: modeData.estimatedCompletionMinutes || 0,
      slotLength: modeData.scheduling.slotLengthInMinutes || 0,
      openingHours: modeData.openingHours || [],
    });

    outlet.patch({
      isLoaded: true,
      id: response.outletId,
      name: response.name,
      slug: response.id,
      phone: response.contact?.phoneNumber,
      isTaxIncluded: !response.taxExclusive,
      timezone: response.openingHours.timezone,
      hasDelivery: response.settings.delivery.enabled,
      hasPickup: response.settings.pickup.enabled,
      hasCurbside: response.settings.curbside.enabled,
      address: {
        location: response.address?.location || "",
        city: response.address?.city || "",
        lat: response.address?.coordinate?.lat || 0,
        lng: response.address?.coordinate?.lng || 0,
      },
      orderingModes: {
        delivery: mapModeSettings("delivery", response.settings.delivery),
        pickup: mapModeSettings("pickup", response.settings.pickup),
        curbside: mapModeSettings("curbside", response.settings.curbside),
      },
      isLoadedOrderingModeSettings: true,
    });

    // Load additional data for the outlet parallelly
    try {
      discounts.load(business.id, outlet.id, routeOrderingMode);
      outlet.loadPickupAcceptsPackaging(business.id, business.salesChannel);
      outlet.refreshPaymentMethods(routeOrderingMode, business.salesChannel);
    } catch {
      // Silence any errors from loading additional data to avoid blocking the main flow
    }

    await Promise.all([
      // Track Google conversion for the outlet is not supporting yet
      customer.trackGoogleConversion(outlet.id, business.salesChannel),
      menus.loadMenus({
        businessId: business.id,
        outletId: outlet.id,
        orderingMode: routeOrderingMode,
        salesChannel: business.salesChannel,
      }),
    ]);

    if (response.id !== routeOutlet) {
      return navigateTo(
        {
          name: to.name,
          params: {
            ...to.params,
            outletSlug: response.id,
          },
          query: to.query,
          hash: to.hash,
        },
        {
          replace: true,
          redirectCode: 301,
        },
      );
    }
  } catch (error: unknown) {
    useBugReporter().report(error, {
      tags: {
        "code.flag": "middleware-store",
      },
    });

    return abortWithUnexpectedError();
  }
});
